/** React Query bindings. One hook per endpoint, one query key per resource. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { Horizon } from '@/api/types'

const MINUTE = 60_000

export const useHealth = () =>
  useQuery({ queryKey: ['health'], queryFn: api.health, refetchInterval: 30_000 })

export const useSymbols = () =>
  useQuery({ queryKey: ['symbols'], queryFn: api.symbols, staleTime: 10 * MINUTE })

export const useWatchlists = () =>
  useQuery({ queryKey: ['watchlists'], queryFn: api.watchlists, refetchInterval: MINUTE })

export const useCandles = (ticker: string | null, timeframe: string, limit = 500) =>
  useQuery({
    queryKey: ['candles', ticker, timeframe, limit],
    queryFn: () => api.candles(ticker!, timeframe, limit),
    enabled: Boolean(ticker),
    // The forming bar changes constantly; the socket drives the live candle,
    // this is just the safety net.
    refetchInterval: 60_000,
  })

export const useOverlays = (ticker: string | null, timeframe: string, enabled: boolean) =>
  useQuery({
    queryKey: ['overlays', ticker, timeframe],
    queryFn: () => api.overlays(ticker!, timeframe),
    enabled: Boolean(ticker) && enabled,
    staleTime: 30_000,
  })

export const useStructure = (ticker: string | null, timeframe: string, enabled: boolean) =>
  useQuery({
    queryKey: ['structure', ticker, timeframe],
    queryFn: () => api.structure(ticker!, timeframe),
    enabled: Boolean(ticker) && enabled,
    staleTime: 60_000,
    retry: false, // 404 means "not enough closed bars", which retrying cannot fix
  })

/** Every open signal across the scanned universe, not just one symbol. */
export const useMarketSignals = () =>
  useQuery({
    queryKey: ['signals', 'market'],
    queryFn: () => api.signals({ open_only: true, limit: 200 }),
    refetchInterval: 60_000,
  })

export const useSignals = (ticker: string | null, timeframe?: string) =>
  useQuery({
    queryKey: ['signals', ticker, timeframe],
    queryFn: () =>
      api.signals({
        ticker: ticker ?? undefined,
        timeframe: timeframe ? [timeframe] : undefined,
        open_only: true,
        limit: 20,
      }),
    enabled: Boolean(ticker),
  })

export const useAllSignals = () =>
  useQuery({
    queryKey: ['signals', 'all'],
    queryFn: () => api.signals({ open_only: true, limit: 50 }),
    refetchInterval: 2 * MINUTE,
  })

export const useRiskFlags = (ticker: string | null) =>
  useQuery({
    queryKey: ['risk-flags', ticker],
    queryFn: () => api.riskFlags(ticker ?? undefined),
    enabled: Boolean(ticker),
    refetchInterval: 5 * MINUTE,
  })

export const useMtf = (ticker: string | null) =>
  useQuery({
    queryKey: ['mtf', ticker],
    queryFn: () => api.mtf(ticker!),
    enabled: Boolean(ticker),
  })

export const useNews = (ticker: string | null) =>
  useQuery({
    queryKey: ['news', ticker],
    queryFn: () => api.news({ ticker: ticker ?? undefined, hours: 72, limit: 40 }),
    refetchInterval: 2 * MINUTE,
  })

export const useEvents = () =>
  useQuery({
    queryKey: ['events'],
    queryFn: () => api.events({ hours_ahead: 96 }),
    refetchInterval: 5 * MINUTE,
  })

export const useBacktestRuns = (ticker?: string) =>
  useQuery({ queryKey: ['backtest', 'runs', ticker], queryFn: () => api.backtestRuns(ticker) })

export const useBacktestDetail = (id: number | null) =>
  useQuery({
    queryKey: ['backtest', 'run', id],
    queryFn: () => api.backtestRun(id!),
    enabled: id !== null,
  })

export function useRunBacktest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { ticker: string; horizon: Horizon; cost_bps?: number }) =>
      api.runBacktest(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backtest'] }),
  })
}

/** `minutes = 0` unmutes. Both paths return the same shape to the caller. */
export function useAddToWatchlist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ watchlistId, ticker }: { watchlistId: number; ticker: string }) =>
      api.addToWatchlist(watchlistId, ticker),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['watchlists'] }),
  })
}

export function useMute() {
  const queryClient = useQueryClient()
  return useMutation<{ ticker: string; muted_until: string | null }, Error, { ticker: string; minutes: number }>({
    mutationFn: ({ ticker, minutes }) =>
      minutes > 0 ? api.mute(ticker, minutes) : api.unmute(ticker),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['watchlists'] }),
  })
}
